import type { App } from '../../app/App';
import { Scene } from '../core/Scene';
import { RULES } from '../combat/rules';
import { button, thumb } from '../../ui/components';
import { h } from '../../ui/dom';
import { doodleSvg } from '../../ui/doodleSvg';
import { t } from '../../i18n';

/** SCREEN 05 — Game design overview as an in-game "How to Play" page. */
export class HelpScene extends Scene {
  constructor(app: App) {
    super(app, 'scene-help page');
  }

  enter(): void {
    const app = this.app;
    const d = RULES.damage;
    const loop = [t('Create Hero'), t('Draw a Monster'), t('Rock Paper Scissors'), t('Earn Rewards'), t('Upgrade'), t('Share'), t('Repeat!')];
    const rps = (a: 'rock' | 'paper' | 'scissors', b: 'rock' | 'paper' | 'scissors', word: string): HTMLElement =>
      h('div', { class: 'rps-rule' }, thumb(app, null, `move:${a}`, 52), h('span', { class: 'beats' }, word), thumb(app, null, `move:${b}`, 52));

    this.dom.append(
      h('div', { class: 'spiral', 'aria-hidden': 'true' }),
      h(
        'header',
        { class: 'help-top' },
        button(t('← Back'), { variant: 'ghost', small: true, sfx: app, onClick: () => history.length > 1 ? void app.scenes.go(app.store.hero ? 'notebook' : 'menu', undefined, 'page-back') : void app.scenes.go('menu', undefined, 'page-back') }),
        h('h1', { class: 'page-heading' }, t('How to Play')),
      ),
      h(
        'div',
        { class: 'help-grid' },
        h(
          'section',
          { class: 'help-card paper-card taped' },
          h('h2', null, t('The Game Loop')),
          h('ol', { class: 'loop-diagram' }, loop.map((step, i) => h('li', { style: { '--i': String(i) } as unknown as Partial<CSSStyleDeclaration> }, step))),
          h('p', null, t('Just like the battles we drew in the back of our notebooks — but the doodles come alive.')),
        ),
        h(
          'section',
          { class: 'help-card sticky-note yellow' },
          h('h2', null, t('Rock · Paper · Scissors')),
          rps('rock', 'scissors', t('smashes')),
          rps('scissors', 'paper', t('snips')),
          rps('paper', 'rock', t('wraps')),
          h('p', null, t('Same move = tie, no damage. Both sides pick at the same time; nobody can peek.')),
          h('p', { class: 'hand-hint' }, t('Keys: 1 / 2 / 3 or R / P / S')),
        ),
        h(
          'section',
          { class: 'help-card paper-card' },
          h('h2', null, t('Damage')),
          h('p', { class: 'formula' }, `${t('Damage')} = max(1, ATK − DEF × 0.5)`),
          h(
            'ul',
            null,
            h('li', null, t('Combo: +{step}% per extra win in a row (max +{cap}%).', { step: d.comboStepPercent, cap: d.comboCapPercent })),
            h('li', null, t('Critical hit: LUCK% chance (max {chance}%) for +{bonus}%.', { chance: d.critChanceCap, bonus: d.critBonusPercent })),
            h('li', null, t('After {rounds} rounds the fighter with more HP (in %) wins.', { rounds: RULES.battle.maxRounds })),
          ),
        ),
        h(
          'section',
          { class: 'help-card paper-card' },
          h('h2', null, t('Skills')),
          h('ul', { class: 'skill-list' }, RULES.skills.filter((s) => s.id !== 'none').map((s) => h('li', null, h('b', null, `${t(s.name)} (${s.cost})`), ` — ${t(s.description)}`))),
        ),
        h(
          'section',
          { class: 'help-card sticky-note pink' },
          h('h2', null, t('Monster Personalities')),
          h('ul', null, RULES.personalities.map((p) => h('li', null, h('b', null, t(p.name)), ` — ${t(p.description)}`))),
          h('p', { class: 'hand-hint' }, t('Monsters decide before you do, based only on earlier rounds.')),
        ),
        h(
          'section',
          { class: 'help-card paper-card' },
          h('h2', null, t('Fair Play')),
          h(
            'ul',
            null,
            h('li', null, t('Public monsters must fit a stat budget: HP ×{hp}, ATK ×{atk}, DEF ×{def}, LUCK ×{luck} + skill cost.', { hp: RULES.budget.costHp, atk: RULES.budget.costAtk, def: RULES.budget.costDef, luck: RULES.budget.costLuck })),
            h('li', null, t('Tiers: {list}.', { list: RULES.budget.tiers.map((x) => `${t(x.name)} ${x.budget}`).join(', ') })),
            h('li', null, t('Online ranked battles scale heroes into a {budget}-point arena budget. The server decides every result.', { budget: RULES.budget.pvpPublicBudget })),
            h('li', null, t('Classroom rooms: the room owner sets custom rules and free stats — fun, but never ranked.')),
          ),
        ),
        h(
          'section',
          { class: 'help-card sticky-note blue' },
          h('h2', null, t('The Notebook')),
          h('p', null, t('Hero profile, monster collection, equipment, quests, memories, friends and settings all live on its pages. Everything saves automatically.')),
          h('p', { class: 'hand-hint' }, t('F3 shows the debug overlay (FPS, frame time, objects).')),
          doodleSvg('pencil', 'help-pencil'),
        ),
      ),
    );
  }
}
